import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Building2, Loader2, Bot, ExternalLink, BarChart3, Copy, Check, KeyRound, LayoutDashboard, Info } from "lucide-react";
import { toast } from "sonner";

import TopNav from "@/components/TopNav";
import { Button } from "@/components/ui/button";
import { listCompanyAgents } from "@/lib/api";
import { useMyCompany, canSeeKeys } from "@/hooks/useMyCompany";
import { ActiveBadge, AgentStateBadge, TokenAlertsBanner, errDetail } from "@/components/company/CompanyUi";
import DepartmentsPanel from "@/components/company/DepartmentsPanel";

export default function MyCompany() {
  const nav = useNavigate();
  const loc = useLocation();
  const { data, loading, refresh } = useMyCompany();
  const [agents, setAgents] = useState(null);
  const [copied, setCopied] = useState(null);
  // Message left by the /mi-empresa/claves redirect; cleared from history so a reload does not show it again.
  const [redirectNotice] = useState(loc.state?.notice || null);

  useEffect(() => {
    refresh();
    if (loc.state?.notice) nav(loc.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const slug = data?.company?.slug;
  useEffect(() => {
    if (!slug) return;
    listCompanyAgents(slug)
      .then((d) => setAgents(d.agents || []))
      .catch((e) => {
        setAgents([]);
        toast.error(errDetail(e, "No se pudieron cargar los agentes."));
      });
  }, [slug]);

  const copy = async (s) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/agentes/${s}`);
      setCopied(s);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast.error("No se pudo copiar el enlace.");
    }
  };

  const c = data?.company;
  return (
    <div className="min-h-screen bg-white text-ink" data-testid="my-company-page">
      <TopNav />
      <div className="mx-auto max-w-6xl px-6 py-12 sm:py-14">
        <p className="eyebrow flex items-center gap-1.5"><Building2 className="h-3 w-3" /> Mi empresa</p>

        {redirectNotice && (
          <div className="mt-4 flex items-start gap-2 border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="redirect-notice">
            <Info className="mt-0.5 h-4 w-4 shrink-0" /> {redirectNotice}
          </div>
        )}

        {loading && !data ? (
          <div className="flex items-center justify-center py-24 text-slate-500"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Cargando…</div>
        ) : !c ? (
          <div className="mt-6 border border-dashed border-slate-300 bg-slate-50 p-10 text-center" data-testid="no-company">
            <p className="font-display text-2xl text-ink">Su usuario no está vinculado a ninguna empresa</p>
            <p className="mt-2 text-sm text-slate-600">Solicite a Inviktus que lo asocie a su empresa para ver sus agentes y métricas.</p>
          </div>
        ) : (
          <>
            <div className="mt-2 flex flex-wrap items-end justify-between gap-6">
              <div>
                <h1 className="font-display text-4xl text-ink sm:text-5xl" data-testid="my-company-name">{c.name}</h1>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <ActiveBadge active={data.company_active} testid="my-company-state" />
                  {data.is_master && (
                    <span className="rounded-sm border border-accent-blue/40 bg-accent-blue/5 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] text-accent-blue"
                          data-testid="is-master-badge">Usuario master</span>
                  )}
                  <span className="font-mono text-[11px] text-slate-500">
                    Agentes activos {c.agents_active}/{c.agents_total}{c.seats_plan ? ` · plan de ${c.seats_plan} seats` : ""}
                    {c.currency ? ` · moneda ${c.currency}` : ""}
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => nav("/dashboard")} className="rounded-sm text-[11px] uppercase tracking-[0.16em]"
                        data-testid="my-company-dashboard">
                  <LayoutDashboard className="mr-1.5 h-3.5 w-3.5" /> Dashboard
                </Button>
                {canSeeKeys(data) && (
                  <Button onClick={() => nav("/mi-empresa/claves")}
                          className="rounded-sm bg-slate-900 text-[11px] uppercase tracking-[0.16em] text-white hover:bg-slate-800"
                          data-testid="my-company-keys-btn">
                    <KeyRound className="mr-1.5 h-3.5 w-3.5" /> Claves de IA
                  </Button>
                )}
              </div>
            </div>

            {data.company_active === false && (
              <div className="mt-6 border border-red-300 bg-red-50 p-4 text-sm text-red-900" data-testid="company-inactive-notice">
                <p className="font-semibold">Sus agentes están desactivados.</p>
                <p className="mt-1">{data.notice?.message || "Su empresa está desactivada en Inviktus: sus agentes no responden. Contacte a Inviktus para reactivarla."}</p>
              </div>
            )}

            <TokenAlertsBanner level={data.token_alert_level} alerts={data.token_alerts}
                               onGoKeys={canSeeKeys(data) ? () => nav("/mi-empresa/claves") : null} />
            {/* fix 13b (10): by design the keys screen is hidden until Inviktus enables it (llm_ui_visible) */}
            {data.is_master && !canSeeKeys(data) && (
              <p className="mt-4 text-[12px] text-slate-600" data-testid="keys-not-enabled-note">
                Claves de IA: su administrador no ha habilitado esta sección.
              </p>
            )}
            {data.is_master && data.has_token_alert && !canSeeKeys(data) && (
              <p className="mt-2 text-[12px] text-slate-600" data-testid="alerts-contact-inviktus">
                Para ampliar los límites de sus claves de IA, contacte a Inviktus.
              </p>
            )}

            <section className="mt-10">
              <p className="eyebrow">Agentes de su empresa</p>
              {!data.is_master && (
                <p className="mt-2 text-[12px] text-slate-600" data-testid="my-agents-membership-note">
                  Puede usar los agentes de los departamentos en los que el usuario master de su empresa incluyó su correo.
                  Si necesita acceso a otro agente, solicítelo a su usuario master.
                </p>
              )}
              {agents === null ? (
                <div className="mt-4 flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Cargando agentes…</div>
              ) : agents.length === 0 ? (
                <p className="mt-4 border border-dashed border-slate-300 bg-slate-50 p-6 text-sm text-slate-500" data-testid="my-agents-empty">
                  Su empresa todavía no tiene agentes.
                </p>
              ) : (
                <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="my-agents-grid">
                  {agents.map((a) => {
                    const url = `${window.location.origin}/agentes/${a.slug}`;
                    return (
                      <article key={a.slug} className="border border-slate-200 bg-white p-5" data-testid={`my-agent-${a.slug}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex min-w-0 items-center gap-3">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center bg-slate-900 text-accent-blue-light"><Bot className="h-4 w-4" /></div>
                            <div className="min-w-0">
                              <p className="text-[10px] uppercase tracking-[0.2em] text-accent-blue">{a.department}</p>
                              <h3 className="font-display text-lg text-ink">{a.name}</h3>
                            </div>
                          </div>
                          <AgentStateBadge active={a.active} testid={`my-agent-state-${a.slug}`} />
                        </div>
                        {!a.active && a.inactive_message && <p className="mt-3 text-[12px] text-red-700">{a.inactive_message}</p>}
                        <p className="mt-4 text-[10px] uppercase tracking-[0.2em] text-slate-500">Enlace público del agente</p>
                        <div className="mt-1.5 flex items-center gap-2 border border-slate-200 bg-slate-50 px-3 py-2">
                          <code className="flex-1 truncate font-mono text-[11px] text-slate-700">{url}</code>
                          <button onClick={() => copy(a.slug)} className="text-slate-500 hover:text-slate-900" title="Copiar enlace"
                                  data-testid={`my-agent-copy-${a.slug}`}>
                            {copied === a.slug ? <Check className="h-4 w-4 text-accent-blue" /> : <Copy className="h-4 w-4" />}
                          </button>
                        </div>
                        <div className="mt-4 flex gap-4 text-[11px] uppercase tracking-[0.16em]">
                          <Link to={`/agentes/${a.slug}`} className="inline-flex items-center gap-1 text-slate-800 hover:text-accent-blue" data-testid={`my-agent-open-${a.slug}`}>
                            <ExternalLink className="h-3 w-3" /> Abrir
                          </Link>
                          <Link to={`/agentes/${a.slug}/metricas`} className="inline-flex items-center gap-1 text-slate-800 hover:text-accent-blue" data-testid={`my-agent-metrics-${a.slug}`}>
                            <BarChart3 className="h-3 w-3" /> Métricas
                          </Link>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </section>

            {/* step 5b: departments, e-mails and seats — only the company master manages them here */}
            {data.is_master && (
              <section className="mt-14 border-t border-slate-200 pt-10" data-testid="my-company-departments">
                <p className="eyebrow">Departamentos y accesos</p>
                <p className="mt-2 max-w-3xl text-sm text-slate-600">
                  Organice a sus colaboradores por departamento y elija qué agentes puede usar cada uno. Usted, como
                  usuario master, tiene acceso a todos los agentes y no ocupa seat.
                </p>
                <div className="mt-6">
                  <DepartmentsPanel slug={c.slug} />
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
